import { CreditCard, ExternalLink, Landmark, Loader2 } from "lucide-react";

const STATUS_BADGE = {
  paid: { label: "Pagado", cls: "bg-emerald-100 text-emerald-700" },
  upcoming: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
};

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  if (amount == null) return "—";
  return Number(amount).toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
  });
}

function FinancedCard({ payment }) {
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        <Landmark size={18} className="text-blue-600" />
        <span className="text-sm font-semibold text-blue-900">
          Pago Financiado
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-blue-600 mb-0.5">Concepto</p>
          <p className="text-sm font-medium text-gray-900">
            {payment.concept || "Programa"}
          </p>
        </div>
        {payment.amount != null && (
          <div>
            <p className="text-xs text-blue-600 mb-0.5">Importe total</p>
            <p className="text-sm font-medium text-gray-900">
              {formatCurrency(payment.amount)}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-blue-600 mb-0.5">Fecha de venta</p>
          <p className="text-sm font-medium text-gray-900">
            {formatDate(payment.date)}
          </p>
        </div>
        {payment.invoice_url && (
          <div>
            <p className="text-xs text-blue-600 mb-0.5">Factura</p>
            <a
              href={payment.invoice_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
            >
              <ExternalLink size={13} />
              Ver factura
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaymentsTab({ payments, isAirtable, loading }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 gap-3">
        <Loader2 size={24} className="animate-spin text-indigo-500" />
        <span className="text-sm text-gray-500">Cargando pagos...</span>
      </div>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-16">
        <CreditCard size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-400">No hay pagos registrados</p>
      </div>
    );
  }

  const financed = payments.filter((p) => p.financed);
  const regular = payments.filter((p) => !p.financed);

  return (
    <div className="space-y-4">
      {isAirtable && (
        <p className="text-xs text-gray-400">Datos cargados desde Airtable</p>
      )}

      {financed.map((p) => (
        <FinancedCard key={p.id} payment={p} />
      ))}

      {regular.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">ID</th>
                <th className="text-left px-4 py-3 font-medium">Concepto</th>
                <th className="text-right px-4 py-3 font-medium">Importe</th>
                <th className="text-left px-4 py-3 font-medium">Fecha</th>
                <th className="text-left px-4 py-3 font-medium">Estado</th>
                {isAirtable && (
                  <th className="text-right px-4 py-3 font-medium">
                    Factura
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {regular.map((p) => {
                const badge = STATUS_BADGE[p.status] || {
                  label: p.status,
                  cls: "bg-gray-100 text-gray-600",
                };
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                      {p.invoice_id || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-900">
                      {p.concept || "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">
                      {formatCurrency(p.amount)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDate(p.date)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    {isAirtable && (
                      <td className="px-4 py-3 text-right">
                        {p.invoice_url ? (
                          <a
                            href={p.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-xs font-medium"
                          >
                            <ExternalLink size={13} />
                            Ver
                          </a>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
